export type SolutionTemplateOrders = Record<string, string[]>;

export function normalizeTemplateOrderKey(value: unknown): string {
  return typeof value === 'string'
    ? value.trim().toLowerCase().replace(/[\s_-]+/g, '-')
    : '';
}

function normalizeTemplateKeyArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<string>();
  const normalizedKeys: string[] = [];

  for (const item of value) {
    const normalizedKey = normalizeTemplateOrderKey(item);
    if (!normalizedKey || seen.has(normalizedKey)) {
      continue;
    }

    seen.add(normalizedKey);
    normalizedKeys.push(normalizedKey);
  }

  return normalizedKeys;
}

export function normalizeSolutionTemplateOrders(value: unknown): SolutionTemplateOrders {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const normalizedOrders: SolutionTemplateOrders = {};

  for (const [solutionKey, templateKeys] of Object.entries(value as Record<string, unknown>)) {
    const normalizedSolutionKey = normalizeTemplateOrderKey(solutionKey);
    if (!normalizedSolutionKey) {
      continue;
    }

    normalizedOrders[normalizedSolutionKey] = normalizeTemplateKeyArray(templateKeys);
  }

  return normalizedOrders;
}

export function getSolutionTemplateOrder(value: unknown, solutionKey: string | null | undefined): string[] {
  if (!solutionKey) {
    return [];
  }

  const normalizedOrders = normalizeSolutionTemplateOrders(value);
  return normalizedOrders[normalizeTemplateOrderKey(solutionKey)] || [];
}

export function updateSolutionTemplateOrder(
  value: unknown,
  solutionKey: string,
  templateKeys: unknown,
): SolutionTemplateOrders {
  const normalizedOrders = normalizeSolutionTemplateOrders(value);
  const normalizedSolutionKey = normalizeTemplateOrderKey(solutionKey);

  if (!normalizedSolutionKey) {
    return normalizedOrders;
  }

  normalizedOrders[normalizedSolutionKey] = normalizeTemplateKeyArray(templateKeys);
  return normalizedOrders;
}

export function applySolutionTemplateOrder<T extends { template_key?: string | null }>(
  templates: T[],
  solutionKey: string | null | undefined,
  value: unknown,
): T[] {
  if (!Array.isArray(templates) || templates.length <= 1 || !solutionKey) {
    return templates;
  }

  const orderedTemplateKeys = getSolutionTemplateOrder(value, solutionKey);
  if (orderedTemplateKeys.length === 0) {
    return templates;
  }

  const orderedTemplates: T[] = [];
  const consumedTemplateKeys = new Set<string>();

  for (const orderedTemplateKey of orderedTemplateKeys) {
    const matchingTemplate = templates.find((template) => normalizeTemplateOrderKey(template.template_key) === orderedTemplateKey);
    if (!matchingTemplate) {
      continue;
    }

    orderedTemplates.push(matchingTemplate);
    consumedTemplateKeys.add(orderedTemplateKey);
  }

  const remainingTemplates = templates.filter(
    (template) => !consumedTemplateKeys.has(normalizeTemplateOrderKey(template.template_key))
  );

  return [...orderedTemplates, ...remainingTemplates];
}

// ============================================================================
// PER-SOLUTION WORKFLOW DISABLEMENT
// ============================================================================
// A workflow is provisioned once per client (client_operational_workflow_instances),
// but the same workflow can be mapped to multiple industry solutions. This map
// records solutions where a workflow is disabled so each industry's tabs/nav can
// toggle workflows independently.
//
// Shape: { [solution_key]: template_key[] }
// ============================================================================

export type SolutionDisabledTemplates = Record<string, string[]>;

export function normalizeSolutionDisabledTemplates(value: unknown): SolutionDisabledTemplates {
  return normalizeSolutionTemplateOrders(value);
}

export function getSolutionDisabledTemplates(
  value: unknown,
  solutionKey: string | null | undefined,
): string[] {
  if (!solutionKey) return [];
  const normalizedKey = normalizeTemplateOrderKey(solutionKey);
  if (!normalizedKey) return [];
  return normalizeSolutionDisabledTemplates(value)[normalizedKey] || [];
}

export function updateSolutionDisabledTemplate(
  value: unknown,
  solutionKey: string,
  templateKey: string,
  disabled: boolean,
): SolutionDisabledTemplates {
  const normalizedSolutionKey = normalizeTemplateOrderKey(solutionKey);
  const normalizedTemplateKey = normalizeTemplateOrderKey(templateKey);
  if (!normalizedSolutionKey || !normalizedTemplateKey) {
    return normalizeSolutionDisabledTemplates(value);
  }

  const normalizedMap = normalizeSolutionDisabledTemplates(value);
  const current = new Set(normalizedMap[normalizedSolutionKey] || []);
  if (disabled) {
    current.add(normalizedTemplateKey);
  } else {
    current.delete(normalizedTemplateKey);
  }

  if (current.size === 0) {
    delete normalizedMap[normalizedSolutionKey];
  } else {
    normalizedMap[normalizedSolutionKey] = [...current];
  }

  return normalizedMap;
}
